import React from 'react';
import { Link, Outlet, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export default function AdminLayout() {
    const { signOut, user, isAdmin, loading } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    if (loading) {
        return <div className="p-8 text-center text-white">Loading...</div>;
    }

    if (!user || !isAdmin) {
        return <Navigate to="/auth/login" replace />;
    }

    const handleSignOut = async () => {
        await signOut();
        navigate('/auth/login');
    };

    const navItems = [
        { name: 'Dashboard', path: '/admin', icon: 'ri-dashboard-line' },
        { name: 'Users', path: '/admin/users', icon: 'ri-user-line' },
        { name: 'Packages', path: '/admin/packages', icon: 'ri-suitcase-line' },
        { name: 'Bookings', path: '/admin/bookings', icon: 'ri-calendar-check-line' },
        { name: 'API Control', path: '/admin/api-control', icon: 'ri-settings-3-line' },
        { name: 'Pricing Control', path: '/admin/pricing-control', icon: 'ri-money-dollar-circle-line' },
    ];

    return (
        <div className="flex h-screen bg-black text-white">
            {/* Sidebar */}
            <div className="w-72 bg-gray-900 border-r border-gray-800 flex flex-col shadow-xl">
                <div className="p-6 border-b border-gray-800 flex items-center justify-center">
                    <Link to="/" className="flex items-center space-x-2">
                        <img
                            src="/favicon.png"
                            alt="Logo"
                            className="h-8 w-auto"
                        />
                        <span className="text-xl font-bold text-logo-gold-400 tracking-tight">Admin Panel</span>
                    </Link>
                </div>

                <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                    <div className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Main Menu
                    </div>
                    {navItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${isActive
                                    ? 'bg-gray-800 text-logo-gold-400 border-l-2 border-logo-gold-400'
                                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                    } `}
                            >
                                <i className={`${item.icon} text-lg mr-3 ${isActive ? 'text-logo-gold-400' : 'text-gray-500 group-hover:text-gray-300'
                                    } `}></i>
                                {item.name}
                            </Link>
                        );
                    })}
                </nav>

                <div className="p-4 border-t border-gray-800">
                    <div className="flex items-center p-3 mb-3 bg-gray-800 rounded-lg">
                        <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-gray-700 flex items-center justify-center text-logo-gold-400 font-bold border border-gray-600">
                                {user?.email?.charAt(0).toUpperCase()}
                            </div>
                        </div>
                        <div className="ml-3 min-w-0">
                            <p className="text-sm font-medium text-white truncate">
                                Admin User
                            </p>
                            <p className="text-xs text-gray-400 truncate">
                                {user?.email}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleSignOut}
                        className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                    >
                        <i className="ri-logout-box-line mr-2"></i>
                        Sign Out
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden bg-black">
                <header className="bg-gray-900 shadow-md border-b border-gray-800 z-10">
                    <div className="px-6 py-4 flex items-center justify-between">
                        <h1 className="text-2xl font-bold text-white">
                            {navItems.find((item) => item.path === location.pathname)?.name || 'Dashboard'}
                        </h1>
                        <div className="flex items-center space-x-4">
                            <button className="p-2 text-gray-400 hover:text-white relative">
                                <i className="ri-notification-3-line text-xl"></i>
                                <span className="absolute top-1.5 right-1.5 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-black"></span>
                            </button>
                        </div>
                    </div>
                </header>
                <main className="flex-1 overflow-auto bg-black p-6">
                    <div className="max-w-7xl mx-auto">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
};


